import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import {
  getAllLands,
  getProjectByID,
  getStateAndCity,
  getAllUsersByProjectId
} from '../../services/api/implementation/impl';
import FormInput from '../../../shared/components/ui/FormInput';
import Button from '../../../shared/components/ui/Button';
import user from '../../../shared/assets/svg/organisation.svg';
import { toast } from 'react-hot-toast';
import { multipleUploadFiles, uploadFile } from '../../features/files';
import Multiselect from 'multiselect-react-dropdown';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

const EditProject = ({ id, onClose, onSubmit, upShift, setUpShift, isSubmitting }) => {
  const userId = Cookies.get('userId');
  const dispatch = useDispatch();
  const fileUrls = useSelector((state) => state.file.fileUrls);
  const MultiFiles = useSelector((state) => state.file.multifiles);

  // Local States
  const [registrationDate, setRegistrationDate] = useState('');
  const [permissionDates, setPermissionDates] = useState({});
  const [numBuildings, setNumBuildings] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [developmentType, setDevelopmentType] = useState('');
  const [city, setCity] = useState('Select');
  const [state, setState] = useState('-');
  const [district, setDistrict] = useState('Select');
  const [tehsil, setTehsil] = useState('Select');
  const [cityDropdownOptions, setCityDropdownOptions] = useState([]);
  const [districtDropdownOptions, setDistrictDropdownOptions] = useState([]);
  const [tehsilDropdownOptions, setTehsilDropdownOptions] = useState([]);

  // React Hook Form
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors }
  } = useForm();

  const currentDevelopmentType = watch('development_type');

  // Fetch Project Data
  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => getProjectByID(id),
    enabled: !!id,
    onError: (error) => {
      console.error('Error fetching project:', error);
      setIsLoading(false);
    }
  });

  // Fetch All Lands
  const { data: lands, isLoading: isLandsLoading } = useQuery({
    queryKey: ['lands:-project'],
    queryFn: getAllLands,
    onError: (error) => {
      console.error('Error fetching lands:', error);
    }
  });

  // Fetch management users for assignment dropdowns
  const { data: managementUsers, isLoading: isLoadingManagementUsers } = useQuery({
    queryKey: ['managementUsers', id],
    queryFn: () => getAllUsersByProjectId(id, 1, 500),
    enabled: !!id
  });

  // Helper Functions
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const convertToISODateWithCurrentTime = (dateString) => {
    if (!dateString || dateString.trim() === '') {
      return null;
    }
    const now = new Date();
    const hours = now.getUTCHours().toString().padStart(2, '0');
    const minutes = now.getUTCMinutes().toString().padStart(2, '0');
    const seconds = now.getUTCSeconds().toString().padStart(2, '0');
    const milliseconds = now.getUTCMilliseconds().toString().padStart(3, '0');
    const dateTimeString = `${dateString}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
    return new Date(dateTimeString).toISOString();
  };

  const formatFiles = (files) => {
    if (!Array.isArray(files) || files.length === 0) {
      return [];
    }
    return files.map((file) => ({
      name: file.name || 'default_name',
      url: file.url || file
    }));
  };

  // Extract All Khasra Numbers from Land's Khasra Array

  // Load Project Data and Prefill Form

  // Track page view
  useEffect(() => {
    mixpanel.track('Edit Project Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (project) {
      // Basic Project Fields
      setValue('name', project.name);
      setValue('tncp_registration_number', project.tncp_registration_number);
      setValue('tncp_registration_date', formatDateForInput(project.tncp_registration_date));
      setValue('start_date', formatDateForInput(project.start_date));
      setValue('tncp_permission_end_date', formatDateForInput(project.tncp_permission_end_date));
      setValue('project_type', project.project_type);
      setValue('land_id', project.land_id);
      setValue('joint_registered_city', project.joint_registered_city);
      setValue('joint_registered_date', formatDateForInput(project.joint_registered_date));
      setValue('joint_registered_pincode', project.joint_registered_pincode);
      setValue('joint_registered_state', project.joint_registered_state);
      setValue('joint_registration_no', project.joint_registration_no);
      setValue('development_type', project.development_type);
      setValue('price_list_share', project.price_list_share);
      setValue('unit_of_measurement', project.measurement_type);
      setValue('google_location_url', project.google_location_link);
      setValue(
        'end_date',
        project?.completion_date && project.completion_date !== '__'
          ? formatDateForInput(project.completion_date)
          : ''
      );
      setValue(
        'estimated_budget',
        project.estimated_budget ? String(Number(project.estimated_budget) / 100000) : ''
      );

      // Assignment and meeting date fields
      setValue(
        'targetMeetingDate',
        project.targetMeetingDate ? String(project.targetMeetingDate) : ''
      );
      setValue(
        'reviewMeetingDate',
        project.reviewMeetingDate ? String(project.reviewMeetingDate) : ''
      );
      setValue('organizerUser_id', project.organizerUser_id || '');
      setValue(
        'customerChangeRequestApprovalUser_id',
        project.customerChangeRequestApprovalUser_id || ''
      );
      setValue('dealAppprovingUser_id', project.dealAppprovingUser_id || '');
      setValue('ucAppprovingUser_id', project.ucAppprovingUser_id || '');

      // Social Media Links
      setValue('insta_link', project.insta_link || '');
      setValue('facebook_link', project.facebook_link || '');
      setValue('twitter_link', project.twitter_link || '');
      setValue('youtube_link', project.youtube_link || '');

      // Joint Development Fields
      if (project.development_type === 'joint') {
        setValue('joint_registered_city', project.joint_registered_city);
        setValue('joint_registered_date', formatDateForInput(project.joint_registered_date));
        setValue('joint_registered_pincode', project.joint_registered_pincode);
        setValue('joint_registered_state', project.joint_registered_state);
        setValue('joint_registration_no', project.joint_registration_no);
      }

      // Buildings
      if (project.buildings && project.buildings.length > 0) {
        setNumBuildings(project.buildings.length);
        project.buildings.forEach((building, index) => {
          // Building-specific fields
          setValue(`permission_no_${index}`, building.permissionNo);
          setValue(`permission_date_${index}`, formatDateForInput(building.permissionDate));
          setValue(`permission_end_date_${index}`, formatDateForInput(building.permissionEndDate));
          setValue(`rera_registration_no_${index}`, building.rera_registration_no);
          setValue(`rera_approval_date_${index}`, formatDateForInput(building.rera_approval_date));
          setValue(`rera_permission_tenure_${index}`, building.rera_permission_tenure);
          setValue(`site_address_${index}`, building.site_address);
          setValue(
            `estimated_budget_${index}`,
            building.estimated_budget ? String(Number(building.estimated_budget) / 100000) : ''
          );

          // Prefill Khasra Numbers from Building's Khasra Building Mapping
          const selectedKhasras =
            building.khasra_building_mapping?.map((mapping) => ({
              id: mapping.khasra.id,
              name: mapping.khasra.number
            })) || [];
          console.log({ selectedKhasras });
          setValue(`khasra_number_${index}`, selectedKhasras);

          // File-related fields
          if (building.layoutPlans) {
            dispatch(
              multipleUploadFiles({
                fileType: `layout_plan_${index}`,
                urls: building.layoutPlans.map((doc) => ({ name: doc.name, url: doc.url }))
              })
            );
          }
          if (building.developmentOrders) {
            dispatch(
              multipleUploadFiles({
                fileType: `development_order_${index}`,
                urls: building.developmentOrders.map((doc) => ({ name: doc.name, url: doc.url }))
              })
            );
          }
          if (building.uploadLetterheads) {
            dispatch(
              multipleUploadFiles({
                fileType: `upload_letterhead_${index}`,
                urls: building.uploadLetterheads.map((doc) => ({
                  name: doc.name,
                  url: doc.url
                }))
              })
            );
          }
          if (building.googleMapImages) {
            setValue(`google_map_image_${index}`, building.googleMapImages);
          }
          if (building.additionalDocuments) {
            dispatch(
              multipleUploadFiles({
                fileType: `additional_documents_${index}`,
                urls: building.additionalDocuments.map((doc) => ({
                  name: doc.name,
                  url: doc.url
                }))
              })
            );
          }
        });
      }

      // Project-level File Fields
      dispatch(
        multipleUploadFiles({
          fileType: 'project_images',
          urls: project.projectImages.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        multipleUploadFiles({
          fileType: 'google_images',
          urls: project.googleImage.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        uploadFile({
          fileType: 'project_brochure',
          url: project.brochures.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        uploadFile({
          fileType: 'tncp_approved_plan',
          url: project.tncp_approved_plan.map((doc) => ({ name: doc.name, url: doc.url })) || []
        })
      );
      dispatch(
        uploadFile({
          fileType: 'tncp_sanctioned_layout_plan',
          url:
            project.tncp_sanctioned_layout_plan.map((doc) => ({
              name: doc.name,
              url: doc.url
            })) || []
        })
      );
      dispatch(
        uploadFile({
          fileType: 'projectSpecification',
          url: project.projectSpecification.map((doc) => ({
            name: doc.name,
            url: doc.url
          }))
        })
      );

      setIsLoading(false);
    }
  }, [project, setValue, dispatch]);

  // Ensure city is set after options are loaded
  useEffect(() => {
    if (project && cityDropdownOptions.length > 0) {
      setValue('joint_registered_city', project.joint_registered_city || '');
    }
  }, [project, cityDropdownOptions, setValue]);

  useEffect(() => {
    // Only run if editing and project has a pincode
    if (project && project.joint_registered_pincode) {
      handlePincodeChange(project.joint_registered_pincode);
    }
  }, [project]);

  const selectedLandId = useWatch({ control, name: 'land_id', defaultValue: project?.land_id });
  const selectedLand = lands?.find((land) => land.id === parseInt(selectedLandId));
  const selectedLandState = selectedLand?.state?.toLowerCase();

  // Use a memo for khasra
  const allKhasraNumbers = React.useMemo(() => {
    if (!selectedLand?.ownerLandMappings) return [];

    const khasraSet = new Set();

    selectedLand.ownerLandMappings.forEach((mapping) => {
      mapping.owner?.ownerKhasraMappings?.forEach(({ khasra }) => {
        if (khasra?.id && khasra?.number) {
          khasraSet.add(JSON.stringify({ id: khasra.id, name: khasra.number }));
        }
      });
    });

    return Array.from(khasraSet).map((str) => JSON.parse(str));
  }, [selectedLand]);

  // Event Handlers
  const handleAddBuilding = useCallback(() => {
    setNumBuildings((prev) => prev + 1);
    setUpShift(upShift + 6);
    // Initialize khasra_number for the new building
    setValue(`khasra_number_${numBuildings}`, []);
  }, [setUpShift, upShift, numBuildings, setValue]);

  const handleRemoveBuilding = useCallback(() => {
    setNumBuildings((prev) => prev - 1);
    setUpShift(upShift - 6);
  }, [setUpShift, upShift]);

  const handleDevelopmentType = (value) => {
    setDevelopmentType(value);
    setValue('development_type', value);

    // Clear joint development fields if switching away from joint
    if (value !== 'joint') {
      setValue('joint_registration_no', '');
      setValue('joint_registered_date', '');
      setValue('joint_registered_pincode', '');
      setValue('joint_registered_state', '');
      setValue('joint_registered_city', '');
    }
  };

  const handlePincodeChange = useCallback(
    async (pincode) => {
      const cleanedPincode = pincode.replace(/[^0-9]/g, '');
      setValue('joint_registered_pincode', cleanedPincode);
      if (cleanedPincode.length === 6) {
        try {
          const pincodeList = await getStateAndCity(cleanedPincode);
          const postOfficeList = pincodeList[0]?.PostOffice || [];
          if (postOfficeList.length > 0) {
            setState(postOfficeList[0].State);

            const uniqueCircles = [...new Set(postOfficeList.map((office) => office.Name))];
            const uniqueDistricts = [...new Set(postOfficeList.map((office) => office.District))];
            const uniqueTehsils = [...new Set(postOfficeList.map((office) => office.Block))];

            setCityDropdownOptions(
              uniqueCircles.map((circle) => ({ value: circle, label: circle }))
            );
            setDistrictDropdownOptions(
              uniqueDistricts.map((dist) => ({ value: dist, label: dist }))
            );
            setTehsilDropdownOptions(uniqueTehsils.map((t) => ({ value: t, label: t })));

            setValue('joint_registered_state', postOfficeList[0].State);
            setValue('joint_registered_city', uniqueCircles[0] || 'Select');
            setValue('district', uniqueDistricts[0] || 'Select');
            setValue('tehsil', uniqueTehsils[0] || 'Select');

            setCity(uniqueCircles[0] || 'Select');
            setDistrict(uniqueDistricts[0] || 'Select');
            setTehsil(uniqueTehsils[0] || 'Select');
          } else {
            // If no post office found, reset fields
            setValue('joint_registered_state', '-');
            setValue('joint_registered_city', 'Select');
            setValue('district', 'Select');
            setValue('tehsil', 'Select');

            setState('-');
            setCity('Select');
            setDistrict('Select');
            setTehsil('Select');
            setCityDropdownOptions([]);
          }
        } catch (error) {
          toast.error('The entered pincode is invalid. Please enter a valid pincode.');
          setValue('joint_registered_state', '-');
          setValue('joint_registered_city', 'Select');
          setValue('district', 'Select');
          setValue('tehsil', 'Select');

          setState('-');
          setCity('Select');
          setDistrict('Select');
          setTehsil('Select');
          setCityDropdownOptions([]);
        }
      }
    },
    [setValue]
  );

  // Registration date & building permission date for "next day" logic
  const getNextDay = (date) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1);
    return newDate.toISOString().split('T')[0];
  };

  const handleRegistrationDateChange = (date) => {
    const endDate = getNextDay(date);
    setRegistrationDate(endDate);
  };

  const handleBuildingPermissionDateChange = (index, date) => {
    const newDate = getNextDay(date);
    setPermissionDates((prev) => ({
      ...prev,
      [index]: newDate
    }));
  };

  const isEmptyValue = (val) => {
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object' && val !== null) return Object.keys(val).length === 0;
    return val === '' || val === null || val === undefined;
  };

  // Helper function to check if state uses Survey terminology
  const isSurveyState = (state) => {
    const surveyStates = [
      'andhra pradesh',
      'telangana',
      'karnataka',
      'kerala',
      'tamil nadu',
      'gujarat'
    ];
    return surveyStates.includes(state?.toLowerCase());
  };

  // Consider a building empty only if ALL fields (including id) are empty
  const isBuildingEmpty = (building) => {
    return Object.values(building).every(isEmptyValue);
  };

  const preparePayload = (data) => {
    const rawBuildings = Array.from({ length: numBuildings }, (_, index) => ({
      permission_no: data[`permission_no_${index}`],
      permission_date: convertToISODateWithCurrentTime(data[`permission_date_${index}`]),
      permission_end_date: convertToISODateWithCurrentTime(data[`permission_end_date_${index}`]),
      layout_plan: formatFiles(
        MultiFiles[`layout_plan_${index}`] || project?.buildings?.[index]?.layoutPlans || []
      ),
      development_order: formatFiles(
        MultiFiles[`development_order_${index}`] ||
        project?.buildings?.[index]?.developmentOrders ||
        []
      ),
      upload_letterhead: formatFiles(
        MultiFiles[`upload_letterhead_${index}`] ||
        project?.buildings?.[index]?.uploadLetterheads ||
        []
      ),
      rera_registration_no: data[`rera_registration_no_${index}`],
      rera_approval_date: convertToISODateWithCurrentTime(data[`rera_approval_date_${index}`]),
      rera_permission_tenure: data[`rera_permission_tenure_${index}`],
      google_map_image: formatFiles(
        fileUrls[`google_map_image_${index}`] || project?.buildings?.[index]?.googleMapImages || []
      ),
      additional_documents: formatFiles(
        MultiFiles[`additional_documents_${index}`] ||
        project?.buildings?.[index]?.additionalDocuments ||
        []
      ),
      site_address: data[`site_address_${index}`],
      estimated_budget: data[`estimated_budget_${index}`]
        ? String(Number(data[`estimated_budget_${index}`]) * 100000)
        : undefined,
      id: project?.buildings?.[index]?.id,
      khasra_number: data[`khasra_number_${index}`] || []
    }));

    // Filter out empty buildings
    const filteredBuildings = rawBuildings.filter((b) => !isBuildingEmpty(b));

    const newPayload = {
      land_id: parseInt(data?.land_id),
      name: data.name,
      tncp_registration_number: data.tncp_registration_number,
      tncp_registration_date: convertToISODateWithCurrentTime(data.tncp_registration_date),
      start_date: convertToISODateWithCurrentTime(data.start_date),
      completion_date: convertToISODateWithCurrentTime(data.end_date),
      tncp_permission_end_date: convertToISODateWithCurrentTime(data.tncp_permission_end_date),
      project_type: data.project_type,
      project_images: formatFiles(MultiFiles.project_images || project?.projectImages || []),
      tncp_approved_plan: formatFiles(
        fileUrls.tncp_approved_plan || project?.tncp_approved_plan || []
      ),
      brochure_files: formatFiles(fileUrls.project_brochure || project?.project_brochure || []),
      google_images: formatFiles(MultiFiles.google_images || []),
      google_location_url: data.google_location_url,
      development_type: data.development_type,
      joint_registration_no: data.joint_registration_no,
      joint_registered_date: convertToISODateWithCurrentTime(data.joint_registered_date),
      joint_registered_pincode: data.joint_registered_pincode,
      joint_registered_city: data.joint_registered_city,
      joint_registered_state: data.joint_registered_state,
      tncp_sanctioned_layout_plan: formatFiles(
        fileUrls.tncp_sanctioned_layout_plan || project?.tncp_sanctioned_layout_plan || []
      ),
      projectSpecification: formatFiles(
        fileUrls.projectSpecification || project?.projectSpecification || []
      ),
      price_list_share: data.price_list_share,
      measurement_type: data.unit_of_measurement,
      estimated_budget: data.estimated_budget
        ? String(Number(data.estimated_budget) * 100000)
        : undefined,
      buildings: filteredBuildings.length > 0 ? filteredBuildings : [],
      // Assignment and meeting date fields (optional)
      ...(data.targetMeetingDate && { targetMeetingDate: parseInt(data.targetMeetingDate) }),
      ...(data.reviewMeetingDate && { reviewMeetingDate: parseInt(data.reviewMeetingDate) }),
      ...(data.organizerUser_id && { organizerUser_id: parseInt(data.organizerUser_id) }),
      ...(data.customerChangeRequestApprovalUser_id && {
        customerChangeRequestApprovalUser_id: parseInt(data.customerChangeRequestApprovalUser_id)
      }),
      ...(data.dealAppprovingUser_id && {
        dealAppprovingUser_id: parseInt(data.dealAppprovingUser_id)
      }),
      ...(data.ucAppprovingUser_id && { ucAppprovingUser_id: parseInt(data.ucAppprovingUser_id) }),
      insta_link: data.insta_link,
      facebook_link: data.facebook_link,
      twitter_link: data.twitter_link,
      youtube_link: data.youtube_link
    };

    if (Array.isArray(newPayload.buildings) && newPayload.buildings.length === 0) {
      delete newPayload.buildings;
    }

    return newPayload;
  };

  const onFormSubmit = (data) => {
    // For each building, if any field except khasra_number is filled, khasra_number is required
    for (let index = 0; index < numBuildings; index++) {
      const buildingFields = [
        data[`permission_no_${index}`],
        data[`permission_date_${index}`],
        data[`permission_end_date_${index}`],
        data[`layout_plan_${index}`],
        data[`development_order_${index}`],
        data[`upload_letterhead_${index}`],
        data[`rera_registration_no_${index}`],
        data[`rera_approval_date_${index}`],
        data[`rera_permission_tenure_${index}`],
        data[`google_map_image_${index}`],
        data[`additional_documents_${index}`],
        data[`site_address_${index}`],
        data[`estimated_budget_${index}`],
        project?.buildings?.[index]?.id
      ];
      const hasAnyValue = buildingFields.some((val) => !isEmptyValue(val));
      if (hasAnyValue) {
        const khasra = data[`khasra_number_${index}`];
        if (!khasra || khasra.length === 0) {
          toast.error(`Khasra/Survey Number is required for building #${index + 1}`);
          return;
        }
      }
    }

    // Validate sum of building estimated budgets does not exceed project estimated budget
    const projectEstimatedBudget = Number(data.estimated_budget) || 0;
    let sumBuildingBudgets = 0;
    for (let index = 0; index < numBuildings; index++) {
      const val = Number(data[`estimated_budget_${index}`]) || 0;
      sumBuildingBudgets += val;
    }
    if (sumBuildingBudgets > projectEstimatedBudget) {
      toast.error(
        'Sum of all building Estimated Budgets cannot exceed the Project Estimated Budget.'
      );
      return;
    }

    const payload = preparePayload(data);
    console.log({ payload });
    onSubmit(payload);
  };

  // Display Loading Indicator if Data is Being Fetched
  useEffect(() => {
    if (!isProjectLoading && !isLandsLoading) {
      setIsLoading(false);
    }
  }, [isProjectLoading, isLandsLoading]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onFormSubmit)}>
      {/* ----------------------- Project Details ----------------------- */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">Project Details</span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="flex space-x-8 justify-between items-start w-full">
            {/* Project Image Preview */}
            {MultiFiles.project_images && MultiFiles.project_images[0]?.url ? (
              <img
                src={MultiFiles.project_images[0].url}
                alt="project_images"
                className="w-[10rem] h-[11rem] bg-center bg-cover object-contain object-center rounded-lg"
              />
            ) : (
              <div className="bg-gray-300 rounded-xl w-[10rem] h-[10.8rem] flex justify-center items-center">
                <img
                  src={user}
                  alt="user_image"
                  className="w-[6rem] bg-center bg-cover object-contain object-center"
                />
              </div>
            )}

            <div className="flex flex-col w-full flex-1 h-full items-stretch gap-6">
              {/* Row 1 */}
              <div className="w-full flex flex-col gap-4 flex-1">
                <div className="flex justify-between space-x-4">
                  <FormInput
                    width={24}
                    register={register}
                    register_key={'land_id'}
                    label={'Land Name'}
                    type={'dropdown'}
                    options={lands?.map((item) => ({
                      value: parseInt(item.id),
                      label: item.name
                    }))}
                    isRequired={true}
                    maxLength={150}
                    minLength={6}
                  />
                  <FormInput
                    width={24}
                    register={register}
                    register_key={'name'}
                    label={'Project Name'}
                    type={'text'}
                    isRequired={true}
                  />
                  <FormInput
                    width={24}
                    register={register}
                    register_key={'tncp_registration_number'}
                    label={'TNCP Registration No'}
                    isRequired={false}
                  />
                  <FormInput
                    width={24}
                    register={register}
                    register_key={'tncp_registration_date'}
                    label={'TNCP Registration Date'}
                    type={'date'}
                    isRequired={false}
                    onType={handleRegistrationDateChange}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="w-full flex justify-between items-center">
                <FormInput
                  width={24}
                  register={register}
                  register_key={'start_date'}
                  label={'Project Start Date'}
                  type={'date'}
                  isRequired={true}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'end_date'}
                  label={'Project End Date'}
                  type={'date'}
                  isRequired={true}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'project_images'}
                  label={'Project Images'}
                  type={'file-secondary'}
                  prefilledFiles={MultiFiles['project_images'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'tncp_permission_end_date'}
                  label={'TNCP Permission End Date'}
                  type={'date'}
                  isRequired={false}
                  CustomFeature={{
                    min: registrationDate
                  }}
                />{' '}
              </div>
              {/* Row 3 */}
              <div className="w-full flex justify-between items-center">
                <FormInput
                  width={24}
                  register={register}
                  register_key={'tncp_approved_plan'}
                  label={'TNCP Permission Document'}
                  type={'file-btn'}
                  prefilledFiles={fileUrls['tncp_approved_plan'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'project_type'}
                  label={'Project Type'}
                  type={'dropdown'}
                  isRequired={true}
                  options={[
                    { value: '', label: 'Select' },
                    { value: 'Residential', label: 'Residential' },
                    { value: 'Commercial', label: 'Commercial' },
                    { value: 'Hybrid', label: 'Hybrid' },
                    { value: 'Industrial', label: 'Industrial' }
                  ]}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'project_brochure'}
                  label={'Project Brochure'}
                  type={'file-btn'}
                  prefilledFiles={fileUrls['project_brochure'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'google_images'}
                  label={'Google Images'}
                  type={'file-secondary'}
                  prefilledFiles={MultiFiles['google_images'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />{' '}
              </div>
              {/* Row 4 */}
              <div className="w-full flex justify-between items-center">
                <FormInput
                  width={24}
                  register={register}
                  register_key={'insta_link'}
                  label={'Instagram Link'}
                  type={'text'}
                  isRequired={false}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'facebook_link'}
                  label={'Facebook Link'}
                  type={'text'}
                  isRequired={false}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'twitter_link'}
                  label={'Twitter Link'}
                  type={'text'}
                  isRequired={false}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'youtube_link'}
                  label={'YouTube Link'}
                  type={'text'}
                  isRequired={false}
                />
              </div>
              <div className="w-full flex justify-start gap-4 items-center">
                <FormInput
                  width={24}
                  register={register}
                  register_key={`google_location_url`}
                  label={'Google Location URL'}
                  type={'text'}
                />

                <FormInput
                  width={24}
                  register={register}
                  register_key={`tncp_sanctioned_layout_plan`}
                  label={'TNCP Sanctioned Layout'}
                  type={'file-btn'}
                  maxFileSize={25 * 1024 * 1024}
                  prefilledFiles={fileUrls['tncp_sanctioned_layout_plan'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={`projectSpecification`}
                  label={'Project Specification'}
                  type={'file-btn'}
                  maxFileSize={25 * 1024 * 1024}
                  prefilledFiles={fileUrls['projectSpecification'] || []}
                  accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'development_type'}
                  label={'Development Type'}
                  type={'dropdown'}
                  options={[
                    { label: 'Select', value: '' },
                    { label: 'Joint', value: 'joint' },
                    { label: 'Company', value: 'company' }
                  ]}
                  onDropdownChange={handleDevelopmentType}
                  isRequired={false}
                />
              </div>

              {/* Joint Development Fields (Registration Number & Date) */}

              <div className="w-full flex justify-start gap-3 items-center flex-wrap">
                {' '}
                {currentDevelopmentType === 'joint' && (
                  <>
                    <FormInput
                      width={24}
                      register={register}
                      register_key={'joint_registration_no'}
                      label={'Joint Registration No'}
                      type={'text'}
                      isRequired={true}
                    />
                    <FormInput
                      width={24}
                      register={register}
                      register_key={'joint_registered_date'}
                      label={'Joint Registration Date'}
                      type={'date'}
                      isRequired={true}
                    />

                    <FormInput
                      width={24}
                      register={register}
                      register_key={'joint_registered_pincode'}
                      label={'Pincode'}
                      type={'text'}
                      maxLength={6}
                      onType={handlePincodeChange}
                    />
                    <FormInput
                      width={24}
                      register={register}
                      isRequired
                      register_key={'joint_registered_state'}
                      label={'State'}
                      errors={errors.state}
                      CustomStyle={{ pointerEvents: 'none' }}
                    />
                    <FormInput
                      width={24}
                      register={register}
                      register_key={'joint_registered_city'}
                      label={'City'}
                      type={'dropdown'}
                      options={cityDropdownOptions}
                      isRequired={true}
                    />
                  </>
                )}
                {/* Price List & Unit of Measurement */}
                <FormInput
                  width={24}
                  register={register}
                  register_key={'price_list_share'}
                  label={'Price List'}
                  type={'dropdown'}
                  options={[
                    { label: 'Select', value: '', disabled: true },
                    { label: 'Yes', value: true },
                    { label: 'No', value: false }
                  ]}
                  isRequired={false}
                />
                <FormInput
                  width={24}
                  register={register}
                  register_key={'unit_of_measurement'}
                  label={'Unit of Measurement'}
                  type={'dropdown'}
                  options={[
                    { label: 'Select', value: '', disabled: true },
                    { label: 'sq/ft', value: 'sq/ft' },
                    { label: 'sq/mt', value: 'sq/mt' }
                  ]}
                  isRequired={false}
                  readOnly={true}
                />
                <div className="flex gap-6 w-[24%]">
                  <div className="relative w-full">
                    <FormInput
                      width={100}
                      register={register}
                      register_key={'estimated_budget'}
                      label={'Project Estimated Budget'}
                      type={'text'}
                      isRequired={true}
                    />
                    <span className="p-1 px-2 absolute right-2 top-[52%] bg-gray-100 rounded-md text-sm text-gray-600">
                      Lakhs
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------- Buildings Loop ----------------------- */}
      <div className="flex flex-col gap-12">
        {Array.from({ length: numBuildings }, (_, index) => (
          <div
            className={`flex flex-col gap-8 ${index < numBuildings - 1 ? 'border-b-[2px]' : ''
              } border-b-gray-200 pb-8`}
            key={index}>
            {/* {numBuildings > 1 && index > 0 && (
              <div className="w-full flex justify-end pb-2 mt-[-1.6rem]">
                <span
                  className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer"
                  onClick={handleRemoveBuilding}>
                  - Remove building details
                </span>
              </div>
            )} */}

            {/* Building Permission Details */}
            <div className="flex flex-col justify-between items-start gap-4">
              <div className="w-full flex flex-col gap-4">
                <div className="flex justify-between space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`permission_no_${index}`}
                    label={'Building Permission No'}
                    type={'text'}
                    isRequired={false}
                  />
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`permission_date_${index}`}
                    type={'date'}
                    isRequired={false}
                    label={'Building Permission Date'}
                    onType={(date) => handleBuildingPermissionDateChange(index, date)}
                  />
                  <FormInput
                    width={30}
                    register={register}
                    register_key={`permission_end_date_${index}`}
                    label={'Building Permission End Date'}
                    type={'date'}
                    isRequired={false}
                    CustomFeature={{
                      min: permissionDates[index] || ''
                    }}
                  />
                </div>

                <div className="flex justify-between space-x-4">
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`layout_plan_${index}`}
                    label={`${isSurveyState(selectedLandState) ? 'Layout Plan' : 'Building Layout Plan'}`}
                    type={'file-secondary'}
                    prefilledFiles={MultiFiles[`layout_plan_${index}`] || []}
                    accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                  />
                  <FormInput
                    width={32}
                    register={register}
                    register_key={`development_order_${index}`}
                    label={'Development Order'}
                    type={'file-secondary'}
                    prefilledFiles={MultiFiles[`development_order_${index}`] || []}
                    accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                  />
                  <FormInput
                    width={30}
                    register={register}
                    register_key={`upload_letterhead_${index}`}
                    label={'Upload Letterhead'}
                    type={'file-secondary'}
                    prefilledFiles={MultiFiles[`upload_letterhead_${index}`] || []}
                    accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                  />
                </div>
              </div>
            </div>

            {/* RERA Details */}
            <div className="flex flex-col justify-between items-start gap-4">
              <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
                RERA Details
              </span>

              <div className="w-full flex flex-col gap-4">
                <div className="w-full flex flex-col gap-4">
                  <div className="flex justify-between space-x-4">
                    <FormInput
                      width={32}
                      register={register}
                      register_key={`rera_registration_no_${index}`}
                      label={'RERA Registration No'}
                      type={'text'}
                      isRequired={false}
                    />
                    <FormInput
                      width={32}
                      register={register}
                      register_key={`rera_approval_date_${index}`}
                      label={'RERA Approval Date'}
                      type={'date'}
                      isRequired={false}
                    />
                    <div className="relative w-[32%]">
                      <FormInput
                        width={100}
                        register={register}
                        register_key={`rera_permission_tenure_${index}`}
                        label={'RERA Permission Tenure'}
                        type={'text'}
                        isRequired={false}
                      />
                      <span className="p-1 px-2 absolute right-2 top-[52%] bg-gray-100 rounded-md text-sm text-gray-600">
                        Years
                      </span>
                    </div>
                  </div>

                  <div className="flex justify-between space-x-4">
                    <FormInput
                      width={32}
                      register={register}
                      register_key={`additional_documents_${index}`}
                      label={'Additional Documents'}
                      type={'file-secondary'}
                      prefilledFiles={MultiFiles[`additional_documents_${index}`] || []}
                      accept={'.pdf, .png, .jpg, .jpeg, .doc '}
                    />

                    {/* Khasra MultiSelect */}
                    <Controller
                      name={`khasra_number_${index}`}
                      control={control}
                      render={({ field }) => (
                        <div className="w-[30rem] mt-2">
                          <label className="block text-sm font-medium text-gray-700">
                            {isSurveyState(selectedLandState) ? 'Survey Numbers' : 'Khasra Numbers'}
                          </label>
                          <Multiselect
                            options={allKhasraNumbers} // All possible Khasra from land
                            selectedValues={field.value} // Building's selected Khasra
                            onSelect={(selectedList) => field.onChange(selectedList)}
                            onRemove={(selectedList) => field.onChange(selectedList)}
                            displayValue="name"
                            placeholder={
                              isSurveyState(selectedLandState) ? 'Select Survey' : 'Select Khasra'
                            }
                            style={{
                              optionContainer: {
                                border: '1px solid #ccc',
                                backgroundColor: 'white',
                                maxHeight: '200px',
                                overflowY: 'auto'
                              },
                              option: {
                                color: 'black',
                                backgroundColor: 'white'
                              },
                              chips: {
                                background: '#2A565E',
                                color: 'white'
                              },
                              searchBox: {
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '5px'
                              }
                            }}
                          />
                          {errors[`khasra_number_${index}`] && (
                            <span className="text-red-500 text-sm">
                              {errors[`khasra_number_${index}`].message}
                            </span>
                          )}
                        </div>
                      )}
                    />

                    <FormInput
                      width={32}
                      register={register}
                      register_key={`site_address_${index}`}
                      label={'Site Address'}
                      type={'text'}
                      isRequired={false}
                    />
                  </div>

                  <div className="flex gap-6">
                    <div className="relative w-[32%]">
                      <FormInput
                        width={100}
                        register={register}
                        register_key={`estimated_budget_${index}`}
                        label={'Estimated Budget (₹)'}
                        type={'text'}
                        isRequired={false}
                      />
                      <span className="p-1 px-2 absolute right-2 top-[52%] bg-gray-100 rounded-md text-sm text-gray-600">
                        Lakhs
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* + Add more Building Button */}
        <div className="w-full flex justify-end border-t-[1px] border-stone-200 pb-2 mt-4">
          <span
            className="text-stone-700 underline underline-offset-2 transition-all duration-300 hover:scale-[102%] cursor-pointer mt-4"
            onClick={handleAddBuilding}>
            + Add more building details
          </span>
        </div>
      </div>

      {/* Assignment and Meeting Date Fields */}
      <div className="flex flex-col justify-between items-start gap-4">
        <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
          Assignment & Meeting Dates
        </span>

        <div className="flex flex-col w-full flex-1 gap-4">
          <div className="w-full flex flex-col gap-4">
            <div className="flex justify-between space-x-4">
              {/* Target Meeting Date */}
              <div
                className="flex-shrink-0 flex flex-col gap-2 text-gray-700"
                style={{ width: '32%' }}>
                <div className="flex justify-between items-start space-x-2 w-fit">
                  <span className="text-gray-600">Target Meeting Date (Day)</span>
                </div>
                <Controller
                  name="targetMeetingDate"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter day (1-28)"
                      className="w-full p-2 border-[1px] border-stone-300 rounded"
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        if (value === '' || /^\d+$/.test(value)) {
                          const day = parseInt(value, 10);
                          // Only allow values between 1 and 28, or empty string
                          if (value === '' || (day >= 1 && day <= 28)) {
                            field.onChange(value);
                          }
                        }
                      }}
                    />
                  )}
                />
              </div>

              {/* Review Meeting Date */}
              <div
                className="flex-shrink-0 flex flex-col gap-2 text-gray-700"
                style={{ width: '32%' }}>
                <div className="flex justify-between items-start space-x-2 w-fit">
                  <span className="text-gray-600">Review Meeting Date (Day)</span>
                </div>
                <Controller
                  name="reviewMeetingDate"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter day (1-28)"
                      className="w-full p-2 border-[1px] border-stone-300 rounded"
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        if (value === '' || /^\d+$/.test(value)) {
                          const day = parseInt(value, 10);
                          // Only allow values between 1 and 28, or empty string
                          if (value === '' || (day >= 1 && day <= 28)) {
                            field.onChange(value);
                          }
                        }
                      }}
                    />
                  )}
                />
              </div>

              {/* Organizer User */}
              <FormInput
                width={32}
                register={register}
                register_key="organizerUser_id"
                label="Organizer User"
                type="dropdown"
                options={
                  managementUsers?.data?.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.level ? ` (${user.level})` : ''}`
                  })) || []
                }
                isRequired={false}
              />
            </div>

            <div className="flex justify-between space-x-4">
              {/* Customer Change Request Approval User */}
              <FormInput
                width={32}
                register={register}
                register_key="customerChangeRequestApprovalUser_id"
                label="Customer Change Request Approval User"
                type="dropdown"
                options={
                  managementUsers?.data?.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.level ? ` (${user.level})` : ''}`
                  })) || []
                }
                isRequired={false}
              />
              {/* Deal Approving User */}
              <FormInput
                width={32}
                register={register}
                register_key="dealAppprovingUser_id"
                label="Deal Approving User"
                type="dropdown"
                options={
                  managementUsers?.data?.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.level ? ` (${user.level})` : ''}`
                  })) || []
                }
                isRequired={false}
              />
              {/* UC Approving User */}
              <FormInput
                width={32}
                register={register}
                register_key="ucAppprovingUser_id"
                label="Unit Consideration Approving User"
                type="dropdown"
                options={
                  managementUsers?.data?.map((user) => ({
                    value: user.id,
                    label: `${user.name}${user.level ? ` (${user.level})` : ''}`
                  })) || []
                }
                isRequired={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="w-full flex justify-end items-center space-x-4">
        <Button type="reset" onClick={onClose} displayType="secondary">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          Save
        </Button>
      </div>
    </form>
  );
};

export default EditProject;
